import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, addTimeEntry, fetchMyWork, fetchPlanningLookups, addOtherTimeEntry, fetchMyOtherEntries, fetchMyWorkSummary, fetchMyProjectBreakdown, startAssignment, acceptTask, setMyAssignmentProgress, removePlannedTaskDay, upsertTaskHoursForDay, markTaskDoneForDay, markAssignmentCompleted, updateOtherLog, deleteOtherLog, fetchProjectsList, fetchClientsList } from '../api';
import { formatMinutesToHM, formatStatusLabel } from '../ui/format';
import { formatHoursDecimalToHM } from '../utils/timeFormat';
import { formatDateEU } from '../utils/dateFormat';
import { isAssignedToCurrentUser } from '../utils/myWorkTaskUtils';
import MyWorkCalendarView from '../components/mywork/MyWorkCalendarView.jsx';
import DatePicker from '../ui/DatePicker.jsx';
import InfoTooltip from '../ui/InfoTooltip.jsx';
import toast from 'react-hot-toast';
import { useAppRefresh } from '../context/AppRefreshContext';
import AlertDialog from '../ui/AlertDialog.jsx';
import { getActiveTheme, isRedFocusThemeName, resolveThemeName } from '../ui/tokens';

function toYMDLocal(d) {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseToYMDLocal(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    // Most APIs return DATEONLY as YYYY-MM-DD
    const m = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (m) return m[0];
  }
  return toYMDLocal(value);
}

function startOfWeekMondayLocal(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = date.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDaysLocal(d, days) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  date.setDate(date.getDate() + Number(days || 0));
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toYMDUTC(d) {
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function listYmdsUtcInclusive(fromYmd, toYmd) {
  const start = new Date(String(fromYmd) + 'T00:00:00Z');
  const end = new Date(String(toYmd) + 'T00:00:00Z');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(toYMDUTC(d));
    if (out.length > 370) break;
  }
  return out;
}

function currentMonthKeyUtc() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function computeWeekDays(rangeKey) {
  const now = new Date();
  const base = rangeKey === 'last_week' ? addDaysLocal(now, -7) : now;
  const monday = startOfWeekMondayLocal(base);
  return Array.from({ length: 7 }, (_, i) => toYMDLocal(addDaysLocal(monday, i)));
}

export default function MyTasks() {
  const { bumpRefresh } = useAppRefresh();
  const themeName = resolveThemeName();
  const theme = getActiveTheme();
  const isAuroraLight = themeName === 'auroraExecutiveLight';
  const isAurora = themeName === 'auroraExecutive' || isAuroraLight;
  const isWarmFocus = isRedFocusThemeName(themeName);

  const [me, setMe] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [postingId, setPostingId] = useState(null);
  const [viewKey, setViewKey] = useState('this_week');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [workRange, setWorkRange] = useState({ rangeStart: '', rangeEnd: '' });
  const [modal, setModal] = useState({ open: false, a: null, hours: 0, minutes15: 0, description: '', descriptionError: '' });
  const [otherModal, setOtherModal] = useState({ open: false, date: toYMDLocal(new Date()), hours: 0, minutes15: 0, managerId: '', clientId: '', projectId: '', jobId: '', taskId: '', description: '', descriptionError: '', error: '', saving: false });
  const [lookups, setLookups] = useState({ managers: [], clients: [], projects: [], jobs: [], tasksByJob: {} });
  const [otherEntries, setOtherEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [confirmDone, setConfirmDone] = useState({ open: false, a: null, ymd: '' });
  const [removeDayConfirm, setRemoveDayConfirm] = useState({ open: false, a: null });
  const [confirmClearHours, setConfirmClearHours] = useState({ open: false, assignmentId: null, ymd: '' });
  const [expandedNotes, setExpandedNotes] = useState({});
  const [collapsedDays, setCollapsedDays] = useState({});
  const [hoursDraftByKey, setHoursDraftByKey] = useState({});
  const [hoursSavingByKey, setHoursSavingByKey] = useState({});
  const [hoursErrorByKey, setHoursErrorByKey] = useState({});
  const [hoursNoteByKey, setHoursNoteByKey] = useState({});
  const [hoursNoteErrorByKey, setHoursNoteErrorByKey] = useState({});
  const [completedAssignmentById, setCompletedAssignmentById] = useState({});
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [logHoursModal, setLogHoursModal] = useState({ open: false, a: null, ymd: '', cardKey: '' });

  const [projectBreakdown, setProjectBreakdown] = useState([]);
  const [projectBreakdownLoading, setProjectBreakdownLoading] = useState(false);

  const [otherEditModal, setOtherEditModal] = useState({ open: false, id: null, date: toYMDLocal(new Date()), hours: 0, minutes15: 0, managerId: '', clientId: '', projectId: '', jobId: '', taskId: '', description: '', descriptionError: '', error: '', saving: false });
  const [otherDeleteConfirm, setOtherDeleteConfirm] = useState({ open: false, e: null });

  const NOTES_PREVIEW_MAX = 160;

  const isMonthView = viewKey === 'month';
  const rangeStart = workRange.rangeStart;
  const rangeEnd = workRange.rangeEnd;

  const todayYmd = useMemo(() => toYMDLocal(new Date()), []);
  const yesterdayYmd = useMemo(() => toYMDLocal(addDaysLocal(new Date(), -1)), []);

  const notDoneText = useMemo(() => {
    const name = String(me?.display_name || '').trim();
    return name === 'Petar' ? 'Nisam odradio' : 'Nisam odradila';
  }, [me?.display_name]);

  // Load me (for gendered labels)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setMe(j?.user || null);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const weekDays = useMemo(() => {
    if (isMonthView) return [];
    if (!rangeStart || !rangeEnd) return [];
    return listYmdsUtcInclusive(rangeStart, rangeEnd).slice(0, 7);
  }, [isMonthView, rangeStart, rangeEnd]);

  const assignmentsByAssignedDay = useMemo(() => {
    const map = new Map();
    const noDue = [];
    (Array.isArray(assignments) ? assignments : []).forEach(a => {
      const assignedYmd = parseToYMDLocal(a?.assigned_for) || parseToYMDLocal(a?.due_date);
      if (!assignedYmd) {
        noDue.push(a);
        return;
      }
      const list = map.get(assignedYmd) || [];
      list.push(a);
      map.set(assignedYmd, list);
    });
    return { map, noDue };
  }, [assignments]);

  const calendarTasksByDay = useMemo(() => {
    const m = new Map();
    for (const [ymd, list] of (assignmentsByAssignedDay?.map || new Map()).entries()) {
      const filtered = (Array.isArray(list) ? list : []).filter(a => isAssignedToCurrentUser(a?.task?.assignees, me));
      if (filtered.length > 0) m.set(ymd, filtered);
    }
    return m;
  }, [assignmentsByAssignedDay, me?.id, me?.user_id]);

  const firstPlannedYmdByAssignmentId = useMemo(() => {
    const m = new Map();
    for (const [ymd, list] of (calendarTasksByDay || new Map()).entries()) {
      for (const a of (Array.isArray(list) ? list : [])) {
        const aid = String(a?.assignment_id || a?.id || '').trim();
        if (!aid) continue;
        const prev = m.get(aid);
        if (!prev || String(ymd) < String(prev)) m.set(aid, ymd);
      }
    }
    return m;
  }, [calendarTasksByDay]);

  // Per-assignment totals for the currently loaded range.
  // Requirement: "Total" should be the sum of all planned hours per day.
  const plannedTotalsByAssignmentId = useMemo(() => {
    const totals = new Map(); // assignmentId -> { totalMinutes: number, dayCount: number }
    const daySets = new Map(); // assignmentId -> Set(ymd)

    (Array.isArray(assignments) ? assignments : []).forEach(a => {
      const assignmentId = String(a?.assignment_id || a?.id || '').trim();
      if (!assignmentId) return;

      const ymd = parseToYMDLocal(a?.assigned_for);
      const minutes = Number(a?.planned_minutes_for_day || 0) || 0;
      if (!ymd || !(minutes > 0)) return;

      const prev = totals.get(assignmentId) || { totalMinutes: 0, dayCount: 0 };
      prev.totalMinutes += minutes;
      totals.set(assignmentId, prev);

      const s = daySets.get(assignmentId) || new Set();
      s.add(String(ymd));
      daySets.set(assignmentId, s);
    });

    for (const [assignmentId, daySet] of daySets.entries()) {
      const prev = totals.get(assignmentId) || { totalMinutes: 0, dayCount: 0 };
      totals.set(assignmentId, { ...prev, dayCount: daySet.size });
    }

    return totals;
  }, [assignments]);

  // Auto-collapse yesterday if nothing was touched
  useEffect(() => {
    if (!yesterdayYmd) return;
    if (!assignmentsByAssignedDay?.map?.has?.(yesterdayYmd)) return;

    setCollapsedDays(prev => {
      if (prev && Object.prototype.hasOwnProperty.call(prev, yesterdayYmd)) return prev;
      const list = assignmentsByAssignedDay.map.get(yesterdayYmd) || [];
      if (!list.length) return prev;
      const untouched = list.every(a => {
        const p = Number(a?.progress || 0) || 0;
        const loggedDay = Number(a?.logged_minutes_for_day || 0) || 0;
        const loggedTotal = Number(a?.logged_minutes_total || 0) || 0;
        return p <= 0 && loggedDay <= 0 && loggedTotal <= 0;
      });
      if (!untouched) return prev;
      return { ...(prev || {}), [yesterdayYmd]: true };
    });
  }, [yesterdayYmd, assignmentsByAssignedDay]);

  const monthDaysWithTasks = useMemo(() => {
    if (!isMonthView) return [];
    const keys = Array.from(assignmentsByAssignedDay.map.keys());
    // Ensure days with only "other" logs also show up in month view.
    (Array.isArray(otherEntries) ? otherEntries : []).forEach(e => {
      const ymd = parseToYMDLocal(e?.entry_date);
      if (ymd) keys.push(ymd);
    });
    keys.sort((a, b) => String(a).localeCompare(String(b)));
    return Array.from(new Set(keys));
  }, [assignmentsByAssignedDay, isMonthView, otherEntries]);

  const otherEntriesByDay = useMemo(() => {
    const m = new Map();
    (Array.isArray(otherEntries) ? otherEntries : []).forEach(e => {
      const ymd = parseToYMDLocal(e?.entry_date);
      if (!ymd) return;
      const list = m.get(ymd) || [];
      list.push(e);
      m.set(ymd, list);
    });
    return m;
  }, [otherEntries]);

  const projectsByClientId = useMemo(() => {
    const map = new Map();
    (Array.isArray(lookups.projects) ? lookups.projects : []).forEach(p => {
      const cid = p?.client_id;
      if (cid == null) return;
      const key = String(cid);
      const list = map.get(key) || [];
      list.push(p);
      map.set(key, list);
    });
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      map.set(k, list);
    }
    return map;
  }, [lookups.projects]);

  const projectToClientId = useMemo(() => {
    const map = new Map();
    (Array.isArray(lookups.projects) ? lookups.projects : []).forEach(p => {
      if (!p?.id) return;
      map.set(String(p.id), p?.client_id != null ? String(p.client_id) : '');
    });
    return map;
  }, [lookups.projects]);

  const dayList = useMemo(() => {
    return isMonthView ? monthDaysWithTasks : weekDays;
  }, [isMonthView, monthDaysWithTasks, weekDays]);

  const daySummary = useMemo(() => {
    const doneStatuses = new Set(['gotovo', 'done', 'completed']);
    const out = new Map();
    for (const ymd of dayList) {
      const allList = assignmentsByAssignedDay.map.get(ymd) || [];

      // Remove tasks that were marked completed on a different day.
      const visibleList = (Array.isArray(allList) ? allList : []).filter(a => {
        const aid = a?.assignment_id || a?.id;
        const completedYmd = completedAssignmentById?.[String(aid)] || '';
        if (!completedYmd) return true;
        return String(completedYmd) === String(ymd);
      });

      const taskCount = visibleList.length;
      const totalLoggedMinutes = visibleList.reduce((sum, a) => {
        const v = (a?.logged_minutes_for_day ?? a?.logged_minutes_total ?? 0);
        return sum + (Number(v) || 0);
      }, 0);

      const allDone = taskCount > 0 && visibleList.every(a => {
        const aid = a?.assignment_id || a?.id;
        const completedYmd = completedAssignmentById?.[String(aid)] || '';
        if (completedYmd && String(completedYmd) === String(ymd)) return true;

        if (a?.completed_for_day || a?.locked_for_day) return true;

        const st = String(a?.status || '').toLowerCase();
        const progress = Number(a?.progress || 0) || 0;
        return doneStatuses.has(st) || progress >= 100;
      });

      const status = taskCount === 0 ? null : (allDone ? 'done' : 'pending');
      out.set(ymd, { taskCount, totalLoggedMinutes, status });
    }
    return out;
  }, [dayList, assignmentsByAssignedDay, completedAssignmentById]);

  function groupTasksByDay(tasks) {
    const groups = [];
    const byDayKey = new Map();

    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      const dayKey = parseToYMDLocal(t?.assigned_for) || parseToYMDLocal(t?.due_date) || '';
      if (!dayKey) continue;

      let g = byDayKey.get(dayKey);
      if (!g) {
        const d = new Date(String(dayKey) + 'T00:00:00');
        const label = `${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][((d.getDay() + 6) % 7)]} — ${formatDateEU(d)}`;
        g = { dayKey, label, tasks: [] };
        byDayKey.set(dayKey, g);
        groups.push(g);
      }
      g.tasks.push(t);
    }

    return groups;
  }

  const orderedTasks = useMemo(() => {
    const ordered = [];
    for (const ymd of (Array.isArray(dayList) ? dayList : [])) {
      const list = assignmentsByAssignedDay.map.get(ymd) || [];
      for (const t of (Array.isArray(list) ? list : [])) ordered.push(t);
    }
    return ordered;
  }, [dayList, assignmentsByAssignedDay]);

  const groupedTasks = useMemo(() => {
    const groups = groupTasksByDay(orderedTasks);
    const byDayKey = new Map(groups.map(g => [String(g.dayKey), g]));

    const formatDayLabel = (ymd) => {
      const d = new Date(String(ymd) + 'T00:00:00');
      return `${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][((d.getDay() + 6) % 7)]} — ${formatDateEU(d)}`;
    };

    return (Array.isArray(dayList) ? dayList : []).map((ymd) => {
      const existing = byDayKey.get(String(ymd)) || null;
      return {
        dayKey: String(ymd),
        label: existing?.label || formatDayLabel(ymd),
        tasks: existing?.tasks || [],
      };
    }).filter((g) => {
      const otherForDay = otherEntriesByDay.get(g.dayKey) || [];
      return (Array.isArray(g.tasks) && g.tasks.length > 0) || otherForDay.length > 0;
    });
  }, [dayList, orderedTasks, otherEntriesByDay]);

  const debugMyWorkGroupingSigRef = useRef('');
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    try {
      const orderedIds = (Array.isArray(orderedTasks) ? orderedTasks : []).map((t) => String(t?.assignment_id || t?.id || '')).join(',');
      const groupSig = (Array.isArray(groupedTasks) ? groupedTasks : []).map((g) => `${String(g?.dayKey || '')}:${Array.isArray(g?.tasks) ? g.tasks.length : 0}`).join('|');
      const sig = `${orderedIds}||${groupSig}`;
      if (sig === debugMyWorkGroupingSigRef.current) return;
      debugMyWorkGroupingSigRef.current = sig;

      // Temporary debug logs for verifying the render path and grouping.
      // Keep payloads small but useful.
      // eslint-disable-next-line no-console
      console.log('[MyWork] orderedTasks', (Array.isArray(orderedTasks) ? orderedTasks : []).slice(0, 200).map((t) => ({
        id: t?.assignment_id || t?.id,
        assigned_for: t?.assigned_for,
        due_date: t?.due_date,
        status: t?.status,
        project: t?.project?.name || t?.project_name,
        job: t?.job?.name || t?.job_name,
        task: t?.task?.name || t?.task_name,
      })));
      // eslint-disable-next-line no-console
      console.log('[MyWork] groupedTasks', (Array.isArray(groupedTasks) ? groupedTasks : []).map((g) => ({
        dayKey: g?.dayKey,
        label: g?.label,
        count: Array.isArray(g?.tasks) ? g.tasks.length : 0,
        taskIds: (Array.isArray(g?.tasks) ? g.tasks : []).slice(0, 200).map((t) => t?.assignment_id || t?.id),
      })));
    } catch {}
  }, [orderedTasks, groupedTasks]);

  async function fetchMyWorkBundle() {
    const monthKey = currentMonthKeyUtc();
    const weekOffset = viewKey === 'next_week' ? 1 : (viewKey === 'last_week' ? -1 : 0);
    const params = isMonthView
      ? { view: 'month', month: monthKey }
      : { view: 'week', offset: weekOffset };
    const w = await fetchMyWork(params);
    const rangeStartNext = String(w?.rangeStart || '');
    const rangeEndNext = String(w?.rangeEnd || '');
    const tasks = Array.isArray(w?.tasks) ? w.tasks : [];
    let nextSummary = null;
    if (rangeStartNext && rangeEndNext) {
      try {
        nextSummary = await fetchMyWorkSummary({ from: rangeStartNext, to: rangeEndNext });
      } catch {}
    }
    return { rangeStart: rangeStartNext, rangeEnd: rangeEndNext, tasks, summary: nextSummary };
  }

  async function reloadMyWork() {
    const b = await fetchMyWorkBundle();
    setAssignments(Array.isArray(b.tasks) ? b.tasks : []);
    setWorkRange({ rangeStart: b.rangeStart, rangeEnd: b.rangeEnd });
    setSummary(b.summary);

    if (b?.rangeStart && b?.rangeEnd) {
      try {
        setProjectBreakdownLoading(true);
        const rows = await fetchMyProjectBreakdown({ startDate: b.rangeStart, endDate: b.rangeEnd });
        setProjectBreakdown(Array.isArray(rows) ? rows : []);
      } catch (_) {
        setProjectBreakdown([]);
      } finally {
        setProjectBreakdownLoading(false);
      }
    }
  }

  async function reloadProjectBreakdown(startDate, endDate) {
    if (!startDate || !endDate) return;
    setProjectBreakdownLoading(true);
    try {
      const rows = await fetchMyProjectBreakdown({ startDate, endDate });
      setProjectBreakdown(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // Non-fatal for page render
      setProjectBreakdown([]);
    } finally {
      setProjectBreakdownLoading(false);
    }
  }

  async function reloadOtherEntries() {
    try {
      const others = await fetchMyOtherEntries(50);
      setOtherEntries(Array.isArray(others) ? others : []);
    } catch {}
  }

  async function ensureLookupsLoaded() {
    if (lookupsLoaded) return;
    const [data, projects] = await Promise.all([
      fetchPlanningLookups(),
      fetchProjectsList(),
    ]);
    const clients = await fetchClientsList({ projectsFallback: projects });
    if (data) {
      setLookups({
        managers: Array.isArray(data.managers) ? data.managers : [],
        clients: Array.isArray(clients) ? clients : [],
        projects: Array.isArray(projects) ? projects : [],
        jobs: Array.isArray(data.jobs) ? data.jobs : [],
        tasksByJob: data.tasksByJob || {},
      });
      setLookupsLoaded(true);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      setAssignments([]);
      setWorkRange({ rangeStart: '', rangeEnd: '' });
      setSummary(null);
      try {
        const b = await fetchMyWorkBundle();
        if (!cancelled) {
          setAssignments(Array.isArray(b.tasks) ? b.tasks : []);
          setWorkRange({ rangeStart: b.rangeStart, rangeEnd: b.rangeEnd });
          setSummary(b.summary);
        }
        // Load project breakdown for the same range
        if (!cancelled && b?.rangeStart && b?.rangeEnd) {
          try {
            setProjectBreakdownLoading(true);
            const rows = await fetchMyProjectBreakdown({ startDate: b.rangeStart, endDate: b.rangeEnd });
            if (!cancelled) setProjectBreakdown(Array.isArray(rows) ? rows : []);
          } catch (_) {
            if (!cancelled) setProjectBreakdown([]);
          } finally {
            if (!cancelled) setProjectBreakdownLoading(false);
          }
        }
        // Load recent other entries
        try {
          const others = await fetchMyOtherEntries(50);
          if (!cancelled) setOtherEntries(Array.isArray(others) ? others : []);
        } catch (e) {
          // Non-fatal for page render
        }
      } catch (e) {
        if (!cancelled) setError('Feature in migration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [viewKey]);

  const weekStrip = (
    <div className="overflow-x-auto">
      <div className="min-w-max flex items-stretch gap-2 md:justify-end">
        {weekDays.map((ymd) => {
          const sum = daySummary.get(ymd) || { taskCount: 0, totalLoggedMinutes: 0, status: null };
          const d = new Date(ymd + 'T00:00:00');
          const dow = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][((d.getDay() + 6) % 7)];
          const label = `${dow} ${formatDateEU(d)}`;
          const statusLabel = sum.status === 'done' ? 'Done' : (sum.status === 'pending' ? 'Pending' : '—');
          const statusClass = sum.status === 'done'
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
            : (sum.status === 'pending'
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300');
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => {
                const el = document.getElementById(`day-${ymd}`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="flex flex-col items-start justify-between rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-900"
              style={{ minWidth: 140 }}
              aria-label={`Jump to ${formatDateEU(d) || ymd}`}
            >
              <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{label}</div>
              <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">
                {sum.taskCount} tasks • {formatMinutesToHM(sum.totalLoggedMinutes)}
              </div>
              <div className={`mt-1 inline-flex items-center px-2 py-0.5 text-[11px] rounded ${statusClass}`}>{statusLabel}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  function patchAssignmentLocal(assignmentId, patch) {
    if (!assignmentId) return;
    setAssignments(prev => (Array.isArray(prev) ? prev : []).map(row => {
      const rid = row?.assignment_id || row?.id;
      if (String(rid) !== String(assignmentId)) return row;
      return { ...row, ...patch };
    }));
  }

  function taskDayKey(assignmentId, ymd) {
    return `${String(assignmentId || '')}|${String(ymd || '')}`;
  }

  function cardDomId(cardKey) {
    const k = String(cardKey || '');
    if (!k) return '';
    return `mywork-card-${encodeURIComponent(k)}`;
  }

  function toggleExpanded(taskKey) {
    const k = String(taskKey || '');
    if (!k) return;
    setExpandedTasks(prev => {
      const next = new Set(prev || undefined);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  function toHoursDecimal(minutes) {
    const m = Number(minutes || 0) || 0;
    const h = m / 60;
    // keep 2 decimals for display/prefill
    return Math.round(h * 100) / 100;
  }

  function openModal(a) {
    setModal({ open: true, a, hours: 0, minutes15: 0, description: '', descriptionError: '' });
  }

  function closeModal() {
    setModal(m => ({ ...m, open: false }));
  }

  function openConfirmDone(a, ymd) {
    setConfirmDone({ open: true, a: a || null, ymd: String(ymd || '') });
  }

  function closeConfirmDone() {
    setConfirmDone({ open: false, a: null, ymd: '' });
  }

  function openRemoveDayConfirm(a) {
    setRemoveDayConfirm({ open: true, a: a || null });
  }

  function closeRemoveDayConfirm() {
    setRemoveDayConfirm({ open: false, a: null });
  }

  async function openOther() {
    setOtherModal({ open: true, date: toYMDLocal(new Date()), hours: 0, minutes15: 0, managerId: '', clientId: '', projectId: '', jobId: '', taskId: '', description: '', descriptionError: '', error: '', saving: false });
    try {
      await ensureLookupsLoaded();
    } catch (e) {
      toast.error('Failed to load lookups');
    }
  }

  function closeOther() {
    setOtherModal(m => ({ ...m, open: false }));
  }

  function openLogHoursModal(a, ymd) {
    const aid = a?.assignment_id || a?.id;
    const ymdForCard = String(ymd || parseToYMDLocal(a?.assigned_for) || todayYmd);
    const key = taskDayKey(aid, ymdForCard);

    // Prefill draft if empty
    setHoursDraftByKey(prev => {
      const has = prev && Object.prototype.hasOwnProperty.call(prev, key);
      if (has) return prev;
      const plannedToday = toHoursDecimal(Number(a?.planned_minutes_for_day || 0));
      const loggedToday = toHoursDecimal(Number(a?.logged_minutes_for_day || 0));
      return { ...(prev || {}), [key]: String((loggedToday > 0 ? loggedToday : plannedToday) || 0) };
    });
    setHoursErrorByKey(prev => ({ ...(prev || {}), [key]: '' }));
    setLogHoursModal({ open: true, a: a || null, ymd: ymdForCard, cardKey: key });
  }

  function closeLogHoursModal() {
    setLogHoursModal({ open: false, a: null, ymd: '', cardKey: '' });
  }

  function openTaskFromCalendar(a, ymd) {
    const aid = a?.assignment_id || a?.id;
    const ymdForCard = String(ymd || parseToYMDLocal(a?.assigned_for) || todayYmd);
    if (!aid || !ymdForCard) return;
    const key = taskDayKey(aid, ymdForCard);

    // Ensure the day group is visible (yesterday can be collapsed).
    setCollapsedDays(prev => ({ ...(prev || {}), [ymdForCard]: false }));

    // Focus the task: expand it and collapse others.
    setExpandedTasks(() => new Set([key]));

    // After expansion state updates, scroll to the task.
    setTimeout(() => {
      const el = document.getElementById(cardDomId(key));
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }

  async function saveOther() {
    const hours = Number(otherModal.hours || 0);
    const minutes15 = Number(otherModal.minutes15 || 0);
    if (![0,15,30,45].includes(minutes15)) { toast.error('Minutes must be 0/15/30/45'); return; }
    if (hours < 0 || hours > 24 || !Number.isInteger(hours)) { toast.error('Hours must be 0-24'); return; }
    const total = hours * 60 + minutes15;
    if (total <= 0) { toast.error('Total minutes must be > 0'); return; }
    const entry_date = otherModal.date;
    const client_id = otherModal.clientId ? Number(otherModal.clientId) : null;
    const project_id = otherModal.projectId ? Number(otherModal.projectId) : null;
    const job_id = otherModal.jobId ? Number(otherModal.jobId) : null;
    const task_id = otherModal.taskId ? Number(otherModal.taskId) : null;
    const for_manager_user_id = otherModal.managerId || null;
    if (!client_id || !project_id || !job_id) { toast.error('Client, Project and Job are required'); return; }
    const description = String(otherModal.description || '').trim();
    if (!description) {
      setOtherModal(m => ({ ...m, descriptionError: 'Notes are required.' }));
      return;
    }
    setOtherModal(m => ({ ...m, saving: true, error: '' }));
    try {
      await addOtherTimeEntry({ entry_date, minutes: total, project_id, job_id, task_id, description, for_manager_user_id });
      toast.success('Logged');
      await reloadMyWork();
      await reloadOtherEntries();
      closeOther();
    } catch (e) {
      const msg = e?.message || 'Failed to log time';
      setOtherModal(m => ({ ...m, error: msg }));
      toast.error(msg);
    }
    finally {
      setOtherModal(m => ({ ...m, saving: false }));
    }
  }

  async function saveModal() {
    const a = modal.a;
    if (!a) return;
    const id = a.assignment_id || a.id;
    const hours = Number(modal.hours || 0);
    const minutes15 = Number(modal.minutes15 || 0);
    if (![0,15,30,45].includes(minutes15)) { toast.error('Minutes must be 0/15/30/45'); return; }
    if (hours < 0 || hours > 24 || !Number.isInteger(hours)) { toast.error('Hours must be 0-24'); return; }
    const total = hours * 60 + minutes15;
    if (total <= 0) { toast.error('Total minutes must be > 0'); return; }
    const entry_date = parseToYMDLocal(a?.assigned_for) || new Date().toISOString().slice(0,10);
    const description = String(modal.description || '').trim();
    if (!description) {
      setModal(m => ({ ...m, descriptionError: 'Notes are required.' }));
      return;
    }
    setPostingId(id);
    try {
      await addTimeEntry({ assignment_id: id, entry_date, minutes: total, description });
      toast.success('Saved');
      await reloadMyWork();
      closeModal();
    } catch (e) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setPostingId(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* Range selector */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
          {rangeStart && rangeEnd ? `${formatDateEU(new Date(rangeStart + 'T00:00:00'))} – ${formatDateEU(new Date(rangeEnd + 'T00:00:00'))}` : ''}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">View</label>
          <select
            value={viewKey}
            onChange={e => setViewKey(e.target.value)}
            className="border rounded px-2 py-1"
            style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
          >
            <option value="this_week">This week</option>
            <option value="last_week">Last week</option>
            <option value="next_week">Next week</option>
            <option value="month">Month</option>
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">My Work</h2>
        <div className="flex items-center space-x-2">
          <button
            className={
              `px-3 py-1 rounded text-sm font-medium `
              + (isAurora || isWarmFocus
                ? 'text-white hover:brightness-95'
                : 'bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200')
            }
            style={(isAurora || isWarmFocus) ? { background: theme.primary } : undefined}
            onClick={openOther}
          >
            Log other time or task
          </button>
        </div>
      </div>

      {/* KPI strip (replaces Summary block) */}
      <div
        className="rounded border border-gray-200 dark:border-gray-700 p-3"
        style={
          isAurora
            ? { background: theme.surface2, borderColor: theme.border }
            : undefined
        }
      >
        <div className="text-sm font-semibold inline-flex items-center gap-2" style={isAurora ? { color: theme.text } : undefined}>
          <span>My Project Breakdown</span>
          <InfoTooltip
            ariaLabel="Project breakdown info"
            text={
              'Project Breakdown shows your planned and logged hours by project for the selected period.\n'
              + '% shows how much of the planned project time has already been logged.'
            }
            maxWidthClassName="w-96"
          />
        </div>
        <div className="text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
          {rangeStart && rangeEnd ? `${formatDateEU(new Date(rangeStart + 'T00:00:00'))} → ${formatDateEU(new Date(rangeEnd + 'T00:00:00'))}` : ''}
        </div>

        {projectBreakdownLoading ? (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>Loading…</div>
        ) : (Array.isArray(projectBreakdown) && projectBreakdown.length > 0 ? (
          <div className="mt-3">
            {/* Desktop/tablet header */}
            <div className="hidden sm:grid grid-cols-4 gap-2 text-xs text-gray-500 dark:text-gray-400" style={isAurora ? { color: theme.mutedText } : undefined}>
              <div>Project</div>
              <div className="text-right">Planned</div>
              <div className="text-right">Logged</div>
              <div className="text-right">%</div>
            </div>
            <div className="mt-2 space-y-2">
              {projectBreakdown.map(r => {
                const planned = Number(r?.planned_minutes || 0) || 0;
                const logged = Number(r?.logged_minutes || 0) || 0;
                const pct = planned > 0 ? Math.round((logged / planned) * 100) : null;
                const pid = r?.project_id;
                const name = r?.project_name || '—';

                const onJump = () => {
                  if (!pid) return;
                  const el = document.querySelector(`[data-project-id="${String(pid)}"]`);
                  if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                };

                return (
                  <button
                    key={String(pid) || name}
                    type="button"
                    onClick={onJump}
                    className="w-full text-left rounded border border-gray-200 dark:border-gray-700 p-2 bg-white dark:bg-gray-800"
                    style={isAurora ? { background: theme.surface, borderColor: theme.border } : undefined}
                  >
                    <div className="sm:grid sm:grid-cols-4 sm:gap-2 sm:items-center">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" style={isAurora ? { color: theme.text } : undefined}>{name}</div>
                      <div className="mt-1 sm:mt-0 text-sm text-gray-700 dark:text-gray-200 sm:text-right" style={isAurora ? { color: theme.mutedText } : undefined}>
                        <span className="sm:hidden text-gray-500 dark:text-gray-400">Planned: </span>{formatMinutesToHM(planned)}
                      </div>
                      <div className="mt-1 sm:mt-0 text-sm text-gray-700 dark:text-gray-200 sm:text-right" style={isAurora ? { color: theme.mutedText } : undefined}>
                        <span className="sm:hidden text-gray-500 dark:text-gray-400">Logged: </span>{formatMinutesToHM(logged)}
                      </div>
                      <div className="mt-1 sm:mt-0 text-sm text-gray-700 dark:text-gray-200 sm:text-right" style={isAurora ? { color: theme.mutedText } : undefined}>
                        <span className="sm:hidden text-gray-500 dark:text-gray-400">%: </span>{pct == null ? '—' : `${pct}%`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
            No activity in selected period.
          </div>
        ))}
      </div>

      {/* Calendar toggle (inline section) */}
      <div className="flex items-center justify-start">
        <button
          type="button"
          className={
            `inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold border `
            + (isAurora || isWarmFocus
              ? 'text-white hover:brightness-95'
              : 'bg-indigo-600 text-white hover:brightness-95 border-indigo-600')
          }
          style={(isAurora || isWarmFocus)
            ? { background: theme.primary, borderColor: theme.primary }
            : undefined
          }
          onClick={() => setCalendarOpen(prev => !prev)}
          aria-expanded={calendarOpen}
        >
          <span>Calendar</span>
          <span className="text-xs" aria-hidden="true">{calendarOpen ? '▲' : '▼'}</span>
        </button>
      </div>

      <div
        className="rounded border border-gray-200 dark:border-gray-700 p-3"
        style={
          isAurora
            ? { background: theme.surface2, borderColor: theme.border }
            : undefined
        }
      >
        {summary ? (
          (() => {
            const planned = summary.summary.planned_assigned_minutes || 0;
            const loggedPlanned = summary.summary.logged_assigned_minutes || 0;
            const loggedOther = summary.summary.logged_other_minutes || 0;
            const loggedTotal = summary.summary.logged_total_minutes || 0;
            const ratioPlanned = planned > 0 ? Math.round((loggedPlanned / planned) * 100) : null;
            const ratioTotal = planned > 0 ? Math.round((loggedTotal / planned) * 100) : null;
            const todayTotal = summary.today.logged_total_minutes || 0;

            const kpis = [
              {
                label: 'Planned',
                value: formatMinutesToHM(planned),
                helpText: 'Planned = hours planned on your assigned tasks for the selected period.',
              },
              {
                label: 'Logged',
                value: formatMinutesToHM(loggedPlanned),
                helpText: 'Logged = hours you actually logged on planned task work in the selected period.',
              },
              { label: 'Other', value: formatMinutesToHM(loggedOther) },
              {
                label: 'Total',
                value: formatMinutesToHM(loggedTotal),
                helpText: 'Total = all hours logged in the selected period, including planned task work and other logged time.',
              },
              {
                label: 'Ratio %',
                value: ratioPlanned != null ? `${ratioPlanned}%` : '—',
                helpText: 'Ratio % = percentage of planned task hours that have been logged.\nFormula: planned logged / planned * 100',
              },
              {
                label: 'Ratio % Total',
                value: ratioTotal != null ? `${ratioTotal}%` : '—',
                helpText: 'Ratio % Total = percentage of total logged hours compared to planned hours.\nFormula: total logged / planned * 100',
              },
              { label: 'Today', value: formatMinutesToHM(todayTotal) },
            ];

            return (
              <div className="flex items-stretch gap-2 overflow-x-auto">
                {kpis.map((k) => (
                  <div
                    key={k.label}
                    className="flex-shrink-0 rounded px-3 py-2 border"
                    style={
                      isAurora
                        ? { background: theme.surfaceElevated || theme.surface, borderColor: theme.border }
                        : undefined
                    }
                  >
                    <div className="text-[11px] inline-flex items-center gap-1" style={isAurora ? { color: theme.mutedText } : undefined}>
                      <span>{k.label}</span>
                      {k.helpText ? (
                        <InfoTooltip
                          ariaLabel={`${k.label} info`}
                          text={k.helpText}
                          maxWidthClassName="w-80"
                        />
                      ) : null}
                    </div>
                    <div className="text-sm font-semibold">{k.value}</div>
                  </div>
                ))}
              </div>
            );
          })()
        ) : (
          <div className="text-sm text-gray-600" style={isAurora ? { color: theme.mutedText } : undefined}>Loading summary…</div>
        )}
      </div>

      {/* Inline calendar section */}
      {calendarOpen && (
        <MyWorkCalendarView
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          tasksByDay={calendarTasksByDay}
          todayYmd={todayYmd}
          isAurora={isAurora}
          theme={theme}
          onSelectTask={openTaskFromCalendar}
        />
      )}

      {loading && <div className="text-sm text-gray-600">Loading assignments…</div>}
      {error && <div className="text-sm text-amber-700">{error}</div>}

      {!loading && assignments.length === 0 && (
        <div className="text-sm text-gray-600">No assignments</div>
      )}

      {/* Assignments grouped by day */}
      <div className="space-y-6">
        {groupedTasks.map(({ dayKey: ymd, label: heading, tasks: allForDay }) => {
          const completedList = (Array.isArray(allForDay) ? allForDay : []).filter(a => {
            const aid = a?.assignment_id || a?.id;
            const completedYmd = completedAssignmentById?.[String(aid)] || '';
            const completedHere = !!completedYmd && String(completedYmd) === String(ymd);
            const doneForDay = !!(a?.completed_for_day || a?.locked_for_day);
            return completedHere || doneForDay;
          });
          const activeList = (Array.isArray(allForDay) ? allForDay : []).filter(a => {
            const aid = a?.assignment_id || a?.id;
            const completedYmd = completedAssignmentById?.[String(aid)] || '';
            const doneForDay = !!(a?.completed_for_day || a?.locked_for_day);
            return !completedYmd && !doneForDay;
          });
          const visibleCount = activeList.length + completedList.length;
          const otherForDay = otherEntriesByDay.get(ymd) || [];
          return (
            <section
              key={ymd}
              id={`day-${ymd}`}
              className="scroll-mt-4"
            >
              <div className="mt-4 mb-3">
                <div
                  className="px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20"
                  style={
                    isAurora
                      ? { borderColor: theme.border, background: theme.surfaceElevated || theme.surface2 }
                      : undefined
                  }
                >
                  <div className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100" style={isAurora ? { color: theme.text } : undefined}>
                    {heading}
                  </div>
                </div>
              </div>

              {(() => {
                const renderCard = (a, ymdContext) => (
                  <div key={a.assignment_id || a.id || `${a.project_id}-${a.job_id}-${a.task_id || 'null'}`}
                       id={(() => {
                         const aidOuter = a?.assignment_id || a?.id;
                         const ymdOuter = ymdContext || parseToYMDLocal(a?.assigned_for) || parseToYMDLocal(a?.due_date) || todayYmd;
                         const key = taskDayKey(aidOuter, ymdOuter);
                         return cardDomId(key);
                       })()}
                       data-project-id={String(a?.project?.id || a?.project_id || '')}
                       className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 sm:p-4 bg-white dark:bg-gray-800 shadow-sm cursor-pointer"
                       style={
                         isAurora
                           ? {
                               background: isAuroraLight ? (theme.surface || '#FFFFFF') : (theme.surfaceElevated || theme.surface2),
                               borderColor: theme.border,
                               boxShadow: `0 1px 2px ${theme.shadow}`,
                             }
                           : undefined
                       }
                       onClick={(e) => {
                         // Expand/collapse when clicking the row itself (not action buttons/inputs).
                         const t = e?.target;
                         if (t && typeof t.closest === 'function') {
                           if (t.closest('button, a, input, select, textarea')) return;
                         }
                         const aid = a?.assignment_id || a?.id;
                         const ymdForCard = ymdContext || parseToYMDLocal(a?.assigned_for) || parseToYMDLocal(a?.due_date) || todayYmd;
                         const cardKey = taskDayKey(aid, ymdForCard);
                         toggleExpanded(cardKey);
                       }}>
                    {(() => {
                        const aid = a.assignment_id || a.id;
                        const projectName = (a.project && a.project.name) || a.project_name || `Project #${a.project_id}`;
                        const clientName = (a.project && (a.project.client_name || a.project.clientName)) || a.client_name || a.clientName || '';
                        const jobName = a.job?.name || a.job_name || a.job_id || '-';
                        const subtaskName = a.task?.name || a.task_name || (a.task_id ? String(a.task_id) : '');
                        const hasSubtask = !!subtaskName && String(subtaskName).trim() && String(subtaskName) !== String(jobName);
                        const title = projectName ? `${jobName} — ${projectName}` : jobName;
                        const subtitle = projectName;
                        const plannedTodayHours = toHoursDecimal(Number(a?.planned_minutes_for_day || 0));

                        const plannedTodayHoursDisplay = plannedTodayHours;

                        const formatHoursShort = (h) => {
                          const n = Number(h);
                          if (!Number.isFinite(n)) return '-';
                          const rounded = Math.round(n * 10) / 10;
                          return Number.isInteger(rounded) ? `${rounded}h` : `${rounded}h`;
                        };

                        const ymdForCard = ymdContext || parseToYMDLocal(a?.assigned_for) || parseToYMDLocal(a?.due_date) || todayYmd;
                        const cardKey = taskDayKey(aid, ymdForCard);
                        const completedYmd = completedAssignmentById?.[String(aid)] || '';
                        const isCompletedHere = !!completedYmd && String(completedYmd) === String(ymdForCard);
                        const isCompletedForDay = !!a?.completed_for_day;
                        const isLockedForDay = !!a?.locked_for_day;
                        const isDoneForDay = isCompletedHere || isCompletedForDay || isLockedForDay;

                        // If marked completed on an earlier day, hide from later planned days (frontend-only behavior)
                        if (completedYmd && String(ymdForCard) > String(completedYmd)) {
                          return null;
                        }

                        const isAccepted = !!(a?.accepted_at || a?.acceptedAt);

                        const stickyStatusLabel = isCompletedHere
                          ? 'Completed'
                          : (isCompletedForDay
                            ? 'Done for day'
                            : (isLockedForDay
                              ? 'Logged'
                              : (formatStatusLabel(a.status) || (isAccepted ? 'Accepted' : '—'))));

                        const timeframeLabel = (() => {
                          const startYmd = parseToYMDLocal(a?.start_date) || parseToYMDLocal(a?.assigned_for) || '';
                          const dueYmd = parseToYMDLocal(a?.due_date) || parseToYMDLocal(a?.start_date) || parseToYMDLocal(a?.assigned_for) || '';
                          if (!startYmd && !dueYmd) return '—';
                          const startText = startYmd ? (formatDateEU(new Date(String(startYmd).slice(0, 10) + 'T00:00:00')) || startYmd) : '—';
                          const dueText = dueYmd ? (formatDateEU(new Date(String(dueYmd).slice(0, 10) + 'T00:00:00')) || dueYmd) : '—';
                          return `${startText} – ${dueText}`;
                        })();

                        const givenBy = a.created_by_manager?.display_name || a.createdBy?.display_name || '-';

                        const notesRaw = typeof a.notes === 'string' ? a.notes.trim() : '';
                        const noteKey = `${String(aid || '')}|${String(a.assigned_for || a.due_date || '')}`;
                        const isExpanded = !!expandedNotes[noteKey];
                        const shouldTruncate = notesRaw.length > NOTES_PREVIEW_MAX;
                        const notesPreview = shouldTruncate && !isExpanded
                          ? `${notesRaw.slice(0, NOTES_PREVIEW_MAX).trimEnd()}…`
                          : notesRaw;

                        const myUserId = String(me?.id || me?.user_id || '').trim();
                        const taskAssigneesRaw = Array.isArray(a?.task?.assignees) ? a.task.assignees : [];

                        const fullAssignees = (() => {
                          // task.assignees is authoritative: show only actual assignees.
                          const normalized = (Array.isArray(taskAssigneesRaw) ? taskAssigneesRaw : []).map(p => ({
                            userId: String(p?.id || p?.user_id || '').trim(),
                            name: String(p?.name || p?.display_name || '').trim(),
                          })).filter(p => !!p.userId);

                          const seen = new Set();
                          const others = [];
                          let meRow = null;

                          for (const p of normalized) {
                            if (seen.has(p.userId)) continue;
                            seen.add(p.userId);
                            if (myUserId && p.userId === myUserId) {
                              meRow = { userId: p.userId, name: 'You', isMe: true };
                            } else {
                              others.push({ userId: p.userId, name: p.name || '—', isMe: false });
                            }
                          }

                          others.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
                          return meRow ? [meRow, ...others] : others;
                        })();

                        const assigneesPreview = fullAssignees.slice(0, 3);
                        const assigneesExtraCount = Math.max(0, fullAssignees.length - assigneesPreview.length);

                        const detailsOpen = expandedTasks instanceof Set ? expandedTasks.has(cardKey) : false;

                        const todayPlannedText = formatHoursShort(plannedTodayHoursDisplay);
                        const todayLoggedText = formatMinutesToHM(Number(a?.logged_minutes_for_day || 0) || 0);
                        const totalSpec = plannedTotalsByAssignmentId.get(String(aid)) || null;
                        const totalMinutes = Number(totalSpec?.totalMinutes || 0) || 0;
                        const totalDays = Number(totalSpec?.dayCount || 0) || 0;
                        const totalHours = totalMinutes > 0 ? toHoursDecimal(totalMinutes) : 0;
                        const totalText = (totalMinutes > 0 && totalDays > 0)
                          ? `${formatHoursShort(totalHours)} (${totalDays}d)`
                          : '';

                        // Other logged aligned by context (same range as Summary)
                        const otherLogged = (() => {
                          const pid = a.project?.id || a.project_id || null;
                          const jid = a.job?.id || a.job_id || null;
                          const tid = a.task?.id || a.task_id || null;
                          const m = (summary?.otherByContext || []).find(x => (x.project_id || null) === (pid || null) && (x.job_id || null) === (jid || null) && ((x.task_id || null) === (tid || null)));
                          return m && m.other_logged_minutes > 0 ? Number(m.other_logged_minutes || 0) : 0;
                        })();

                        return (
                          <>
                            {/* Collapsed summary (default) */}
                            <div className="space-y-2">
                              {/* Row 1 */}
                              <div
                                className={
                                  "flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
                                  + (detailsOpen
                                    ? " sticky top-4 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                                    : "")
                                }
                                style={
                                  detailsOpen && isAurora
                                    ? {
                                        background: isAuroraLight ? (theme.surface || '#FFFFFF') : (theme.surfaceElevated || theme.surface2),
                                        borderColor: theme.border,
                                      }
                                    : undefined
                                }
                              >
                                <div className="min-w-0">
                                  <div className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug truncate" style={isAurora ? { color: theme.text } : undefined}>
                                    {title}
                                  </div>
                                  {detailsOpen && subtitle ? (
                                    <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 truncate" style={isAurora ? { color: theme.mutedText } : undefined}>
                                      {subtitle}
                                    </div>
                                  ) : null}
                                  <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100" style={isAurora ? { color: theme.text } : undefined}>
                                    {timeframeLabel}
                                  </div>
                                  <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400" style={isAurora ? { color: theme.mutedText } : undefined}>
                                    {detailsOpen ? 'Click to collapse' : 'Click to expand'}
                                  </div>
                                </div>
                                <div className="flex flex-col sm:items-end gap-2">
                                  {detailsOpen ? (
                                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                                      <span
                                        className="inline-flex items-center px-2 py-0.5 rounded text-[11px] border border-gray-300 dark:border-gray-700"
                                        style={isAurora ? { borderColor: theme.border, color: theme.text } : undefined}
                                      >
                                        {stickyStatusLabel}
                                      </span>
                                    </div>
                                  ) : null}
                                  <div className="text-sm text-gray-700 dark:text-gray-200" style={isAurora ? { color: theme.mutedText } : undefined}>
                                    Today planned: {todayPlannedText} <span className="text-gray-400 dark:text-gray-500">|</span> Logged: {todayLoggedText}
                                    {totalText ? (
                                      <> <span className="text-gray-400 dark:text-gray-500">|</span> Total: {totalText}</>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                                    {!isAccepted && !isDoneForDay && (
                                      <button
                                        type="button"
                                        className="px-3 py-1.5 rounded text-sm font-medium border border-amber-300 text-amber-900 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-100 dark:hover:bg-amber-900/20 disabled:opacity-60"
                                        style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                                        disabled={postingId === aid || !aid}
                                        onClick={async () => {
                                          if (!aid) return;
                                          try {
                                            setPostingId(aid);
                                            const resp = await acceptTask({ assignmentId: aid });
                                            const acceptedAt = resp?.accepted_at || resp?.acceptedAt || new Date().toISOString();
                                            patchAssignmentLocal(aid, { accepted_at: acceptedAt });
                                            bumpRefresh();
                                            await reloadMyWork();
                                            toast.success('Accepted');
                                          } catch (e) {
                                            toast.error(e?.message || 'Failed to accept task');
                                          } finally {
                                            setPostingId(null);
                                          }
                                        }}
                                      >
                                        Accept
                                      </button>
                                    )}

                                    {!isCompletedHere && !isCompletedForDay && (
                                      <button
                                        type="button"
                                        className={
                                          `px-3 py-1.5 rounded text-sm font-medium text-white disabled:opacity-60 `
                                          + (isAurora || isWarmFocus ? '' : 'bg-gray-900 hover:bg-gray-800')
                                        }
                                        style={(isAurora || isWarmFocus) ? { background: theme.primary } : undefined}
                                        disabled={!aid}
                                        onClick={() => openLogHoursModal(a, ymdForCard)}
                                      >
                                        Log hours
                                      </button>
                                    )}

                                    {!isCompletedHere && !isCompletedForDay && !isLockedForDay && (
                                      <button
                                        type="button"
                                        className="px-3 py-1.5 rounded text-sm font-medium border border-green-300 text-green-800 hover:bg-green-50 dark:border-green-700 dark:text-green-200 dark:hover:bg-green-900/20 disabled:opacity-60"
                                        disabled={postingId === aid || !aid}
                                        onClick={async () => {
                                          if (!aid) return;
                                          try {
                                            setPostingId(aid);
                                            await markTaskDoneForDay({ assignmentId: aid, dateISO: ymdForCard });
                                            bumpRefresh();
                                            await reloadMyWork();
                                          } catch (e) {
                                            toast.error(e?.message || 'Failed to mark done for today');
                                          } finally {
                                            setPostingId(null);
                                          }
                                        }}
                                      >
                                        Done for today
                                      </button>
                                    )}

                                  </div>
                                  {isAccepted && !isDoneForDay && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] border border-gray-300 dark:border-gray-700" style={isAurora ? { borderColor: theme.border, color: theme.text } : undefined}>
                                        Accepted
                                      </span>
                                    </div>
                                  )}
                                  {isCompletedHere && (
                                    <div className="text-xs text-purple-800 dark:text-purple-200">Completed</div>
                                  )}
                                  {!isCompletedHere && isCompletedForDay && (
                                    <div className="text-xs text-green-800 dark:text-green-200">Done for day</div>
                                  )}
                                  {!isCompletedHere && !isCompletedForDay && isLockedForDay && (
                                    <div className="text-xs text-gray-700 dark:text-gray-200">Logged</div>
                                  )}
                                </div>
                              </div>

                              {/* Row 2 */}
                              <div className="flex flex-col gap-1 text-xs text-gray-700 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                {hasSubtask ? (
                                  <div className="min-w-0 truncate text-sm" style={isAurora ? { color: theme.text } : undefined}>
                                    <span className="text-gray-500 dark:text-gray-400">Subtask:</span>{' '}
                                    <span className="font-semibold">{subtaskName}{clientName ? ` — ${clientName}` : ''}</span>
                                  </div>
                                ) : null}
                                <div className="min-w-0 truncate">
                                  <span className="text-gray-500 dark:text-gray-400">Project:</span> {subtitle}
                                </div>
                              </div>

                              {/* Row 3 */}
                              <div className="text-xs text-gray-700 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                <span className="text-gray-500 dark:text-gray-400">Assignees:</span>{' '}
                                {assigneesPreview.map((p, idx) => (
                                  <span key={`${p.userId || p.name || idx}`} className={p.isMe ? 'font-semibold text-gray-900 dark:text-gray-100' : ''} style={isAurora && p.isMe ? { color: theme.text } : undefined}>
                                    {p.isMe ? 'You' : p.name}{idx < assigneesPreview.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                                {assigneesExtraCount > 0 ? (
                                  <span> (+{assigneesExtraCount})</span>
                                ) : null}
                              </div>

                              {/* Manager note preview (collapsed) */}
                              {!detailsOpen && notesRaw ? (
                                <div
                                  className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-100"
                                  style={
                                    isAurora
                                      ? { borderColor: theme.warning || theme.border, background: `${(theme.warning || theme.border)}20`, color: theme.text }
                                      : undefined
                                  }
                                >
                                  <span className="font-medium">Manager note:</span>{' '}
                                  {notesRaw.length > 160 ? `${notesRaw.slice(0, 160).trimEnd()}…` : notesRaw}
                                </div>
                              ) : null}

                              {/* Expanded details (optional) */}
                              {detailsOpen && (
                                <div className="mt-2 space-y-2">
                                  <div className="text-xs text-gray-700 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                    <span className="text-gray-500 dark:text-gray-400">Given by:</span> {givenBy}
                                    <span className="text-gray-400 dark:text-gray-500"> • </span>
                                    <span className="text-gray-500 dark:text-gray-400">Status:</span> {formatStatusLabel(a.status) || '-'}
                                    {otherLogged > 0 ? (
                                      <>
                                        <span className="text-gray-400 dark:text-gray-500"> • </span>
                                        <span className="text-gray-500 dark:text-gray-400">Other:</span> {formatMinutesToHM(otherLogged)}
                                      </>
                                    ) : null}
                                  </div>

                                  <div className="text-xs text-gray-700 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                                    <span className="text-gray-500 dark:text-gray-400">Assignees:</span> {fullAssignees.map(p => (p.isMe ? 'You' : p.name)).join(', ')}
                                  </div>

                                  {!isCompletedHere && (
                                    <div className="pt-2">
                                      <button
                                        type="button"
                                        className="px-3 py-2 rounded text-sm font-medium border border-purple-300 text-purple-800 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-200 dark:hover:bg-purple-900/20 disabled:opacity-60"
                                        disabled={postingId === aid || !aid}
                                        onClick={() => openConfirmDone(a, ymdForCard)}
                                      >
                                        Mark whole task as done
                                      </button>
                                    </div>
                                  )}

                                  {notesRaw && (
                                    <div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</div>
                                      <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                                        {notesPreview}
                                      </div>
                                      {shouldTruncate && (
                                        <button
                                          type="button"
                                          className="mt-1 text-xs text-indigo-700 dark:text-indigo-200 hover:underline"
                                          onClick={() => setExpandedNotes(prev => ({ ...prev, [noteKey]: !prev[noteKey] }))}
                                        >
                                          {isExpanded ? 'Show less' : 'Show more'}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()}
                  </div>
                );

                return (
                  <>
                    {visibleCount === 0 && otherForDay.length === 0 && (
                      <div className="text-sm text-gray-500">No tasks</div>
                    )}

                    {activeList.length > 0 && (
                      <div className="grid grid-cols-1 gap-3">
                        {activeList.map(a => renderCard(a, ymd))}
                      </div>
                    )}

                    {completedList.length > 0 && (
                      <details
                        className="mt-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        style={isAurora ? { background: theme.surfaceElevated || theme.surface2, borderColor: theme.border } : undefined}
                      >
                        <summary
                          className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100"
                          style={isAurora ? { color: theme.text } : undefined}
                        >
                          Completed tasks ({completedList.length})
                        </summary>
                        <div className="px-4 pb-4">
                          <div className="grid grid-cols-1 gap-3">
                            {completedList.map(a => renderCard(a, ymd))}
                          </div>
                        </div>
                      </details>
                    )}

                    {otherForDay.length > 0 && (
                      <div
                        className="mt-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        style={isAurora ? { background: theme.surfaceElevated || theme.surface2, borderColor: theme.border } : undefined}
                      >
                        <div className="px-4 pt-3">
                          <div className="text-sm font-semibold" style={isAurora ? { color: theme.text } : undefined}>Other logs</div>
                          <div className="text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                            {otherForDay.length} entr{otherForDay.length === 1 ? 'y' : 'ies'}
                          </div>
                        </div>
                        <div className="px-4 pb-4 mt-2 space-y-2">
                          {otherForDay.map(e => {
                            const labelProject = e.project ? `${e.project.name || '—'}${e.project.client_name ? ` — ${e.project.client_name}` : ''}` : '—';
                            const labelCat = e.job?.name || '—';
                            const labelTask = e.task?.name ? ` • ${e.task.name}` : '';
                            const note = e.description ? String(e.description) : '';
                            return (
                              <div key={e.id} className="rounded border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div className="text-sm text-gray-700 dark:text-gray-200" style={isAurora ? { color: theme.text } : undefined}>
                                    {formatMinutesToHM(e.minutes || 0)} • {labelProject}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-700 disabled:opacity-60"
                                      style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                                      disabled={postingId === e.id}
                                      onClick={async () => {
                                        try {
                                          await ensureLookupsLoaded();
                                        } catch (err) {
                                          toast.error('Failed to load lookups');
                                          return;
                                        }
                                        const total = Number(e.minutes || 0) || 0;
                                        const h = Math.floor(total / 60);
                                        const m15 = total % 60;
                                        const pid = e.project?.id || '';
                                        const cid = pid ? (projectToClientId.get(String(pid)) || '') : '';
                                        setOtherEditModal({
                                          open: true,
                                          id: e.id,
                                          date: parseToYMDLocal(e.entry_date) || toYMDLocal(new Date()),
                                          hours: h,
                                          minutes15: [0,15,30,45].includes(m15) ? m15 : 15,
                                          managerId: e.forManager?.id || '',
                                          clientId: cid,
                                          projectId: pid,
                                          jobId: e.job?.id || '',
                                          taskId: e.task?.id || '',
                                          description: e.description || '',
                                          error: '',
                                          saving: false,
                                        });
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-900/20 disabled:opacity-60"
                                      disabled={postingId === e.id}
                                      onClick={() => setOtherDeleteConfirm({ open: true, e })}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1" style={isAurora ? { color: theme.mutedText } : undefined}>
                                  {labelCat}{labelTask}
                                </div>
                                {e.forManager && (
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                      For: {e.forManager.display_name || e.forManager.id}
                                    </span>
                                  </div>
                                )}
                                {note && (
                                  <div className="text-sm text-gray-700 dark:text-gray-300 mt-1 break-words">
                                    {note}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </section>
          );
        })}

        {isMonthView && dayList.length === 0 && !loading && (
          <div className="text-sm text-gray-600">No assignments in this month</div>
        )}

        {assignmentsByAssignedDay.noDue.length > 0 && (
          <section className="space-y-2">
            <div className="text-sm font-semibold">No due date</div>
            <div className="grid grid-cols-1 gap-3">
              {assignmentsByAssignedDay.noDue.map((a) => (
                <div key={a.assignment_id || a.id || `${a.project_id}-${a.job_id}-${a.task_id || 'null'}`}
                     className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5 bg-white dark:bg-gray-800 shadow-sm">
                  {(() => {
                    const aid = a.assignment_id || a.id;
                    const projectName = (a.project && a.project.name) || a.project_name || `Project #${a.project_id}`;
                    const clientName = (a.project && (a.project.client_name || a.project.clientName)) || a.client_name || a.clientName || '';
                    const jobName = a.job?.name || a.job_name || a.job_id || '-';
                    const subtaskName = a.task?.name || a.task_name || (a.task_id ? String(a.task_id) : '');
                    const hasSubtask = !!subtaskName && String(subtaskName).trim() && String(subtaskName) !== String(jobName);
                    const title = projectName ? `${jobName} — ${projectName}` : jobName;
                    const subtitle = projectName;
                    const plannedLabel = a.planned_hours != null ? formatHoursDecimalToHM(a.planned_hours) : '-';
                    const loggedLabel = formatMinutesToHM(a.logged_minutes_total || 0);
                    const progress = Math.max(0, Math.min(100, Number(a.progress || 0) || 0));
                    const givenBy = a.created_by_manager?.display_name || a.createdBy?.display_name || '-';
                    const notesRaw = typeof a.notes === 'string' ? a.notes.trim() : '';
                    const noteKey = `${String(aid || '')}|no_due`;
                    const isExpanded = !!expandedNotes[noteKey];
                    const shouldTruncate = notesRaw.length > NOTES_PREVIEW_MAX;
                    const notesPreview = shouldTruncate && !isExpanded
                      ? `${notesRaw.slice(0, NOTES_PREVIEW_MAX).trimEnd()}…`
                      : notesRaw;
                    return (
                      <>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug truncate">{title}</div>
                            {hasSubtask && (
                              <div className="mt-1 text-base text-gray-700 dark:text-gray-200 truncate">
                                <span className="text-gray-500 dark:text-gray-400">Subtask:</span>{' '}
                                <span className="font-semibold">{subtaskName}{clientName ? ` — ${clientName}` : ''}</span>
                              </div>
                            )}
                            <div className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 truncate">{subtitle}</div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 text-xs font-semibold whitespace-nowrap">Planned: {plannedLabel}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 text-xs whitespace-nowrap">Logged: {loggedLabel}</span>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-700 dark:text-gray-300">
                          <div className="flex items-center gap-2"><span className="text-gray-500 dark:text-gray-400">Assignee</span><span className="font-medium">You</span></div>
                          <div className="flex items-center gap-2"><span className="text-gray-500 dark:text-gray-400">Due</span><span className="font-medium">-</span></div>
                          <div className="flex items-center gap-2"><span className="text-gray-500 dark:text-gray-400">Given by</span><span className="font-medium">{givenBy}</span></div>
                          <div className="flex items-center gap-2"><span className="text-gray-500 dark:text-gray-400">Status</span><span className="font-medium">{formatStatusLabel(a.status) || '-'}</span></div>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300"><div className="text-gray-500 dark:text-gray-400">Progress</div><div className="font-medium">{progress}%</div></div>
                          <div className="mt-1 h-2 w-full rounded bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className="h-2 bg-indigo-600" style={{ width: `${progress}%` }} /></div>
                        </div>
                        {notesRaw && (
                          <div className="mt-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</div>
                            <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{notesPreview}</div>
                            {shouldTruncate && (
                              <button type="button" className="mt-1 text-xs text-indigo-700 dark:text-indigo-200 hover:underline" onClick={() => setExpandedNotes(prev => ({ ...prev, [noteKey]: !prev[noteKey] }))}>
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>


      <AlertDialog
        open={confirmDone.open}
        title="Confirm whole task completion"
        description="This will mark the whole task as done and remove it from remaining days. Continue?"
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        busy={postingId === (confirmDone.a?.assignment_id || confirmDone.a?.id)}
        onCancel={closeConfirmDone}
        onConfirm={async () => {
          const a = confirmDone.a;
          const aid = a?.assignment_id || a?.id;
          if (!aid) return;
          try {
            setPostingId(aid);
            await markAssignmentCompleted({ assignmentId: aid });
            const ymd = String(confirmDone.ymd || parseToYMDLocal(a?.assigned_for) || todayYmd);
            setCompletedAssignmentById(prev => ({ ...(prev || {}), [String(aid)]: ymd }));
            bumpRefresh();
            toast.success('Marked whole task as done');
            await reloadMyWork();
            closeConfirmDone();
          } catch (e) {
            toast.error(e?.message || 'Failed to mark whole task as done');
          } finally {
            setPostingId(null);
          }
        }}
      />

      <AlertDialog
        open={confirmClearHours.open}
        title="Clear today’s hours?"
        description="This will set today’s logged hours for this task to 0. Continue?"
        confirmLabel="Clear"
        cancelLabel="Cancel"
        busy={postingId === (confirmClearHours.assignmentId || null)}
        onCancel={() => setConfirmClearHours({ open: false, assignmentId: null, ymd: '' })}
        onConfirm={async () => {
          const aid = confirmClearHours.assignmentId;
          const ymd = String(confirmClearHours.ymd || todayYmd);
          if (!aid) return;
          try {
            setPostingId(aid);
            await upsertTaskHoursForDay({ assignmentId: aid, dateISO: ymd, hours: 0 });
            patchAssignmentLocal(aid, { logged_minutes_for_day: 0 });
            setHoursDraftByKey(prev => ({ ...(prev || {}), [taskDayKey(aid, ymd)]: '0' }));
            bumpRefresh();
            await reloadMyWork();
            setConfirmClearHours({ open: false, assignmentId: null, ymd: '' });
            if (logHoursModal?.open && String(logHoursModal?.a?.assignment_id || logHoursModal?.a?.id || '') === String(aid)) {
              closeLogHoursModal();
            }
            toast.success('Cleared');
          } catch (e) {
            toast.error(e?.message || 'Failed to clear');
          } finally {
            setPostingId(null);
          }
        }}
      />

      <AlertDialog
        open={removeDayConfirm.open}
        title="Remove this day from the task plan?"
        description="This is only possible if no hours are logged for this day."
        confirmLabel="Remove day"
        cancelLabel="Cancel"
        busy={postingId === (removeDayConfirm.a?.assignment_id || removeDayConfirm.a?.id)}
        onCancel={closeRemoveDayConfirm}
        onConfirm={async () => {
          const a = removeDayConfirm.a;
          const aid = a?.assignment_id || a?.id;
          const ymd = parseToYMDLocal(a?.assigned_for);
          if (!aid || !ymd) return;
          try {
            setPostingId(aid);
            await removePlannedTaskDay(aid, ymd);
            bumpRefresh();
            toast.success('Day removed');
            await reloadMyWork();
            closeRemoveDayConfirm();
          } catch (e) {
            const status = e?.status;
            const msg = String(e?.message || '');
            if (status === 409 && msg === 'DAY_HAS_LOGGED_TIME') {
              toast.error('Can’t remove: hours already logged for this day.');
            } else {
              toast.error(e?.message || 'Failed to remove day');
            }
          } finally {
            setPostingId(null);
          }
        }}
      />

      {modal.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[6px] flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Log minutes</h3>
            <div className="flex items-center gap-3 mb-3">
              <label className="text-sm">Hours</label>
              <input
                type="number"
                min={0}
                max={24}
                step={1}
                value={modal.hours}
                onChange={e => setModal(m => ({ ...m, hours: e.target.value }))}
                className="w-20 border rounded px-2 py-1 text-black"
              />
              <label className="text-sm">Minutes</label>
              <select
                value={modal.minutes15}
                onChange={e => setModal(m => ({ ...m, minutes15: Number(e.target.value) }))}
                className="border rounded px-2 py-1 text-black"
              >
                {[0,15,30,45].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <textarea
                placeholder="Notes (required)"
                value={modal.description}
                onChange={e => setModal(m => ({ ...m, description: e.target.value, descriptionError: '' }))}
                className="w-full border rounded px-2 py-1 h-20 text-black"
              />
              {modal.descriptionError && (
                <div className="mt-1 text-sm text-red-600">{modal.descriptionError}</div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={closeModal} className="px-3 py-1 rounded border">Cancel</button>
              <button onClick={saveModal} className="px-3 py-1 rounded bg-indigo-600 text-white">Save</button>
            </div>
          </div>
        </div>
      )}

      {otherModal.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[6px] flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Log other time or task</h3>
            {otherModal.error && (
              <div className="mb-2 text-sm text-red-600">{otherModal.error}</div>
            )}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm">Date</label>
                <DatePicker
                  value={otherModal.date}
                  onChange={(v) => setOtherModal(m => ({ ...m, date: v }))}
                  className=""
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm">Hours</label>
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={1}
                  value={otherModal.hours}
                  onChange={e => setOtherModal(m => ({ ...m, hours: e.target.value }))}
                  className="w-20 border rounded px-2 py-1 text-black"
                />
                <label className="text-sm">Minutes</label>
                <select
                  value={otherModal.minutes15}
                  onChange={e => setOtherModal(m => ({ ...m, minutes15: Number(e.target.value) }))}
                  className="border rounded px-2 py-1 text-black"
                >
                  {[0,15,30,45].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Manager (optional)</label>
                <select
                  value={otherModal.managerId}
                  onChange={e => setOtherModal(m => ({ ...m, managerId: e.target.value }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">—</option>
                  {lookups.managers.map(m => (
                    <option key={m.id} value={m.id}>{m.display_name || m.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Client</label>
                <select
                  value={otherModal.clientId}
                  onChange={e => setOtherModal(m => ({ ...m, clientId: e.target.value, projectId: '' }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">Select client…</option>
                  {lookups.clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Project</label>
                <select
                  value={otherModal.projectId}
                  onChange={e => setOtherModal(m => ({ ...m, projectId: e.target.value }))}
                  disabled={!otherModal.clientId}
                  className="w-full border rounded px-2 py-1 text-black disabled:opacity-60"
                >
                  <option value="">{otherModal.clientId ? 'Select project…' : 'Select client first…'}</option>
                  {(projectsByClientId.get(String(otherModal.clientId)) || []).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Job</label>
                <select
                  value={otherModal.jobId}
                  onChange={e => setOtherModal(m => ({ ...m, jobId: e.target.value, taskId: '' }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">Select job…</option>
                  {lookups.jobs.map(j => (
                    <option key={j.id} value={j.id}>{j.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Task (optional)</label>
                <select
                  value={otherModal.taskId}
                  onChange={e => setOtherModal(m => ({ ...m, taskId: e.target.value }))}
                  disabled={!otherModal.jobId}
                  className="w-full border rounded px-2 py-1 text-black disabled:opacity-60"
                >
                  <option value="">—</option>
                  {(lookups.tasksByJob[Number(otherModal.jobId)] || []).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <textarea
                  placeholder="Notes (required)"
                  value={otherModal.description}
                  onChange={e => setOtherModal(m => ({ ...m, description: e.target.value, descriptionError: '' }))}
                  className="w-full border rounded px-2 py-1 h-20 text-black"
                />
                {otherModal.descriptionError && (
                  <div className="mt-1 text-sm text-red-600">{otherModal.descriptionError}</div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={closeOther} className="px-3 py-1 rounded border">Cancel</button>
              <button onClick={saveOther} disabled={otherModal.saving} className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-60">{otherModal.saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {otherEditModal.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[6px] flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Edit other log</h3>
            {otherEditModal.error && (
              <div className="mb-2 text-sm text-red-600">{otherEditModal.error}</div>
            )}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm">Date</label>
                <DatePicker
                  value={otherEditModal.date}
                  onChange={(v) => setOtherEditModal(m => ({ ...m, date: v }))}
                  className=""
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm">Hours</label>
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={1}
                  value={otherEditModal.hours}
                  onChange={e => setOtherEditModal(m => ({ ...m, hours: e.target.value }))}
                  className="w-20 border rounded px-2 py-1 text-black"
                />
                <label className="text-sm">Minutes</label>
                <select
                  value={otherEditModal.minutes15}
                  onChange={e => setOtherEditModal(m => ({ ...m, minutes15: Number(e.target.value) }))}
                  className="border rounded px-2 py-1 text-black"
                >
                  {[0,15,30,45].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Manager (optional)</label>
                <select
                  value={otherEditModal.managerId}
                  onChange={e => setOtherEditModal(m => ({ ...m, managerId: e.target.value }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">—</option>
                  {lookups.managers.map(m => (
                    <option key={m.id} value={m.id}>{m.display_name || m.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Client</label>
                <select
                  value={otherEditModal.clientId}
                  onChange={e => setOtherEditModal(m => ({ ...m, clientId: e.target.value, projectId: '' }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">Select client…</option>
                  {lookups.clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Project</label>
                <select
                  value={otherEditModal.projectId}
                  onChange={e => setOtherEditModal(m => ({ ...m, projectId: e.target.value }))}
                  disabled={!otherEditModal.clientId}
                  className="w-full border rounded px-2 py-1 text-black disabled:opacity-60"
                >
                  <option value="">{otherEditModal.clientId ? 'Select project…' : 'Select client first…'}</option>
                  {(projectsByClientId.get(String(otherEditModal.clientId)) || []).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Job</label>
                <select
                  value={otherEditModal.jobId}
                  onChange={e => setOtherEditModal(m => ({ ...m, jobId: e.target.value, taskId: '' }))}
                  className="w-full border rounded px-2 py-1 text-black"
                >
                  <option value="">Select job…</option>
                  {lookups.jobs.map(j => (
                    <option key={j.id} value={j.id}>{j.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Task (optional)</label>
                <select
                  value={otherEditModal.taskId}
                  onChange={e => setOtherEditModal(m => ({ ...m, taskId: e.target.value }))}
                  disabled={!otherEditModal.jobId}
                  className="w-full border rounded px-2 py-1 text-black disabled:opacity-60"
                >
                  <option value="">—</option>
                  {(lookups.tasksByJob[Number(otherEditModal.jobId)] || []).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <textarea
                  placeholder="Notes (required)"
                  value={otherEditModal.description}
                  onChange={e => setOtherEditModal(m => ({ ...m, description: e.target.value, descriptionError: '' }))}
                  className="w-full border rounded px-2 py-1 h-20 text-black"
                />
                {otherEditModal.descriptionError && (
                  <div className="mt-1 text-sm text-red-600">{otherEditModal.descriptionError}</div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setOtherEditModal(m => ({ ...m, open: false }))} className="px-3 py-1 rounded border">Cancel</button>
              <button
                onClick={async () => {
                  const hours = Number(otherEditModal.hours || 0);
                  const minutes15 = Number(otherEditModal.minutes15 || 0);
                  if (![0,15,30,45].includes(minutes15)) { toast.error('Minutes must be 0/15/30/45'); return; }
                  if (hours < 0 || hours > 24 || !Number.isInteger(hours)) { toast.error('Hours must be 0-24'); return; }
                  const total = hours * 60 + minutes15;
                  if (total <= 0) { toast.error('Total minutes must be > 0'); return; }
                  const entry_date = otherEditModal.date;
                  const clientId = otherEditModal.clientId ? Number(otherEditModal.clientId) : null;
                  const projectId = otherEditModal.projectId ? Number(otherEditModal.projectId) : null;
                  const jobId = otherEditModal.jobId ? Number(otherEditModal.jobId) : null;
                  const taskId = otherEditModal.taskId ? Number(otherEditModal.taskId) : null;
                  const managerId = otherEditModal.managerId || null;
                  if (!clientId || !projectId || !jobId) { toast.error('Client, Project and Job are required'); return; }
                  const note = String(otherEditModal.description || '').trim();
                  if (!note) {
                    setOtherEditModal(m => ({ ...m, descriptionError: 'Notes are required.' }));
                    return;
                  }
                  setOtherEditModal(m => ({ ...m, saving: true, error: '' }));
                  try {
                    await updateOtherLog({
                      id: otherEditModal.id,
                      projectId,
                      jobId,
                      taskId,
                      managerId,
                      dateISO: entry_date,
                      hours: total / 60,
                      note,
                    });
                    toast.success('Updated');
                    await reloadOtherEntries();
                    await reloadMyWork();
                    setOtherEditModal({ open: false, id: null, date: toYMDLocal(new Date()), hours: 0, minutes15: 0, managerId: '', clientId: '', projectId: '', jobId: '', taskId: '', description: '', descriptionError: '', error: '', saving: false });
                  } catch (e) {
                    const msg = e?.message || 'Failed to update other log';
                    setOtherEditModal(m => ({ ...m, error: msg }));
                    toast.error(msg);
                  } finally {
                    setOtherEditModal(m => ({ ...m, saving: false }));
                  }
                }}
                disabled={otherEditModal.saving}
                className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-60"
              >
                {otherEditModal.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log hours modal */}
      {logHoursModal?.open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={closeLogHoursModal}
          />
          <div
            className="relative w-full sm:max-w-lg bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700"
            style={isAurora ? { background: theme.surface, borderColor: theme.border, boxShadow: `0 10px 25px ${theme.shadow}` } : undefined}
          >
            <div className="p-4 sm:p-5">
              {(() => {
                const a = logHoursModal?.a;
                const ymdForModal = logHoursModal?.ymd;
                const cardKey = logHoursModal?.cardKey;
                const aid = a?.assignment_id || a?.id;

                const formatHoursShortModal = (h) => {
                  const n = Number(h);
                  if (!Number.isFinite(n)) return '-';
                  const rounded = Math.round(n * 10) / 10;
                  return `${rounded}h`;
                };

                const plannedTodayField = (a?.plannedTodayHours != null) ? Number(a.plannedTodayHours) : null;
                const plannedTodayFromMinutes = toHoursDecimal(Number(a?.planned_minutes_for_day || 0));
                const plannedToday = Number.isFinite(plannedTodayField) ? plannedTodayField : plannedTodayFromMinutes;
                const loggedTodayMinutes = Number(a?.logged_minutes_for_day || 0) || 0;
                const loggedTodayHM = formatMinutesToHM(loggedTodayMinutes);

                const draft = String((hoursDraftByKey && cardKey && hoursDraftByKey[cardKey] != null) ? hoursDraftByKey[cardKey] : '');
                const err = String((hoursErrorByKey && cardKey && hoursErrorByKey[cardKey]) || '');
                const noteDraft = String((hoursNoteByKey && cardKey && hoursNoteByKey[cardKey] != null) ? hoursNoteByKey[cardKey] : '');
                const noteErr = String((hoursNoteErrorByKey && cardKey && hoursNoteErrorByKey[cardKey]) || '');
                const saving = !!(hoursSavingByKey && cardKey && hoursSavingByKey[cardKey]);

                const title = a?.task_title || a?.title || (a?.job?.name) || a?.job_name || 'Task';
                const dueLabel = a?.due_date ? formatDateEU(new Date(String(a.due_date).slice(0, 10) + 'T00:00:00')) : '-';
                const assignedForLabel = (() => {
                  const ymd = ymdForModal || parseToYMDLocal(a?.assigned_for) || '';
                  if (!ymd) return '-';
                  return formatDateEU(new Date(String(ymd).slice(0, 10) + 'T00:00:00')) || ymd;
                })();
                const canRemoveDay = !!aid && !!ymdForModal && (Number(a?.logged_minutes_for_day || 0) || 0) <= 0;

                return (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate" style={isAurora ? { color: theme.text } : undefined}>
                          Log hours
                        </div>
                        <div className="mt-0.5 text-sm text-gray-700 dark:text-gray-200 truncate" style={isAurora ? { color: theme.mutedText } : undefined}>
                          {title}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300" style={isAurora ? { color: theme.mutedText } : undefined}>
                          Assigned for: {assignedForLabel} <span className="text-gray-400 dark:text-gray-500">•</span> Due: {dueLabel}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 dark:border-gray-700"
                        style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                        onClick={closeLogHoursModal}
                      >
                        Close
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded border border-gray-200 dark:border-gray-700 px-3 py-2" style={isAurora ? { background: theme.surface2, borderColor: theme.border, color: theme.text } : undefined}>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400" style={isAurora ? { color: theme.mutedText } : undefined}>Planned today</div>
                        <div className="text-sm font-medium">{formatHoursShortModal(plannedToday)}</div>
                      </div>
                      <div className="rounded border border-gray-200 dark:border-gray-700 px-3 py-2" style={isAurora ? { background: theme.surface2, borderColor: theme.border, color: theme.text } : undefined}>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400" style={isAurora ? { color: theme.mutedText } : undefined}>Logged today</div>
                        <div className="text-sm font-medium">{loggedTodayHM}</div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" style={isAurora ? { color: theme.text } : undefined}>
                        Hours for today
                      </label>
                      <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-2">
                        <input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          max={24}
                          step={0.25}
                          value={draft}
                          disabled={saving}
                          onChange={(e) => {
                            const v = e.target.value;
                            setHoursDraftByKey(prev => ({ ...(prev || {}), [cardKey]: v }));
                            setHoursErrorByKey(prev => ({ ...(prev || {}), [cardKey]: '' }));
                          }}
                          className="w-full sm:w-40 border rounded px-3 py-2 text-base"
                          style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                          aria-label="Hours for today"
                        />
                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            type="button"
                            className={
                              `flex-1 sm:flex-none px-4 py-2 rounded text-sm font-medium text-white disabled:opacity-60 `
                              + (isAurora || isWarmFocus ? '' : 'bg-gray-900 hover:bg-gray-800')
                            }
                            style={(isAurora || isWarmFocus) ? { background: theme.primary } : undefined}
                            disabled={saving || !aid}
                            onClick={async () => {
                              if (!aid) return;
                              const hoursNum = Number(draft);
                              if (!Number.isFinite(hoursNum) || hoursNum < 0 || hoursNum > 24) {
                                setHoursErrorByKey(prev => ({ ...(prev || {}), [cardKey]: 'Enter hours between 0 and 24' }));
                                return;
                              }

                              const note = String(noteDraft || '').trim();
                                if (hoursNum > 0 && !note) {
                                  setHoursNoteErrorByKey(prev => ({ ...(prev || {}), [cardKey]: 'Notes are required when logging hours.' }));
                                return;
                              }

                              setHoursSavingByKey(prev => ({ ...(prev || {}), [cardKey]: true }));
                              setHoursErrorByKey(prev => ({ ...(prev || {}), [cardKey]: '' }));
                              setHoursNoteErrorByKey(prev => ({ ...(prev || {}), [cardKey]: '' }));
                              try {
                                await upsertTaskHoursForDay({ assignmentId: aid, dateISO: ymdForModal, hours: hoursNum, description: note });
                                patchAssignmentLocal(aid, { logged_minutes_for_day: Math.round(hoursNum * 60) });
                                bumpRefresh();
                                await reloadMyWork();
                                closeLogHoursModal();
                              } catch (e) {
                                const msg = String(e?.message || 'Failed to save hours');
                                setHoursErrorByKey(prev => ({ ...(prev || {}), [cardKey]: msg }));
                              } finally {
                                setHoursSavingByKey(prev => ({ ...(prev || {}), [cardKey]: false }));
                              }
                            }}
                          >
                            {saving ? 'Saving…' : (loggedTodayMinutes > 0 ? 'Update' : 'Save')}
                          </button>

                          {loggedTodayMinutes > 0 && (
                            <button
                              type="button"
                              className="flex-1 sm:flex-none px-4 py-2 rounded text-sm font-medium border border-gray-300 dark:border-gray-700 disabled:opacity-60"
                              style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                              disabled={saving || !aid}
                              onClick={() => setConfirmClearHours({ open: true, assignmentId: aid, ymd: ymdForModal })}
                            >
                              Clear today
                            </button>
                          )}
                        </div>
                      </div>
                      {err && (
                        <div className="mt-2 text-sm text-red-600 dark:text-red-400">{err}</div>
                      )}
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400" style={isAurora ? { color: theme.mutedText } : undefined}>
                        Saving replaces the previous value.
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200" style={isAurora ? { color: theme.text } : undefined}>
                        Notes
                      </label>
                      <textarea
                        value={noteDraft}
                        disabled={saving}
                        onChange={(e) => {
                          const v = e.target.value;
                          setHoursNoteByKey(prev => ({ ...(prev || {}), [cardKey]: v }));
                          setHoursNoteErrorByKey(prev => ({ ...(prev || {}), [cardKey]: '' }));
                        }}
                        className="mt-1 w-full border rounded px-3 py-2 text-base"
                        style={isAurora ? { background: theme.surface, color: theme.text, borderColor: theme.border } : undefined}
                        placeholder="Notes (required)"
                        rows={3}
                      />
                      {noteErr && (
                        <div className="mt-1 text-sm text-red-600 dark:text-red-400">{noteErr}</div>
                      )}
                    </div>

                    {!a ? null : (
                      <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700" style={isAurora ? { borderColor: theme.border } : undefined}>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100" style={isAurora ? { color: theme.text } : undefined}>Task actions</div>
                        <div className="mt-2 flex flex-col sm:flex-row gap-2">
                          {canRemoveDay && (
                            <button
                              type="button"
                              className="px-3 py-2 rounded text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-200 dark:hover:bg-red-900/20 disabled:opacity-60"
                              disabled={postingId === aid}
                              onClick={() => {
                                closeLogHoursModal();
                                openRemoveDayConfirm(a);
                              }}
                            >
                              Remove day
                            </button>
                          )}

                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={otherDeleteConfirm.open}
        title="Delete other log?"
        description="This will delete the selected other log. Continue?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        busy={postingId === (otherDeleteConfirm.e?.id || null)}
        onCancel={() => setOtherDeleteConfirm({ open: false, e: null })}
        onConfirm={async () => {
          const e = otherDeleteConfirm.e;
          if (!e?.id) return;
          try {
            setPostingId(e.id);
            await deleteOtherLog({ id: e.id });
            setOtherEntries(prev => (Array.isArray(prev) ? prev : []).filter(x => String(x?.id) !== String(e.id)));
            toast.success('Deleted');
            await reloadMyWork();
            setOtherDeleteConfirm({ open: false, e: null });
          } catch (err) {
            toast.error(err?.message || 'Failed to delete');
          } finally {
            setPostingId(null);
          }
        }}
      />

    </div>
  );
}
